import {
  U2000_TIMESHEET_WEEK_DAYS,
  type U2000TimesheetWeekDay,
} from '../../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import type { U3129HonorariumItem } from '../U3129Service';
import type { U3129HonorariumLineRef, U3129HonorariumProjectGroup } from './u3129GroupHonorariumForDisplay';

export type U3129HonorariumHourCellCoord = {
  projectKey: string;
  index: number;
  day: U2000TimesheetWeekDay;
};

const HONORARIUM_HOUR_FIELD_ID_RE = /^honoraria\.(\d+)\.(monday|tuesday|wednesday|thursday|friday|saturday|sunday)$/;

/** Ligne avec champs d’heures éditables (aligné sur `HonorariumRowEditor` / `hasProjectSelection`). */
export function u3129HonorariumLineHasEditableHourCells(row: U3129HonorariumItem): boolean {
  const pid = row.projectId?.trim() || row.project?.id?.trim() || '';
  return pid.length > 0;
}

/** Parse l’`id` HTML du champ `U2000InputDecimal` (`honoraria.{index}.{day}`). */
export function u3129ParseHonorariumHourFieldId(fieldId: string): Omit<U3129HonorariumHourCellCoord, 'projectKey'> | null {
  const match = HONORARIUM_HOUR_FIELD_ID_RE.exec(fieldId.trim());
  if (!match) {
    return null;
  }
  const index = Number.parseInt(match[1], 10);
  const day = match[2] as U2000TimesheetWeekDay;
  if (!Number.isFinite(index) || !(U2000_TIMESHEET_WEEK_DAYS as readonly string[]).includes(day)) {
    return null;
  }
  return { index, day };
}

export function u3129HonorariumHourFieldId(index: number, day: U2000TimesheetWeekDay): string {
  return `honoraria.${index}.${day}`;
}

function flattenEditableLines(group: U3129HonorariumProjectGroup): U3129HonorariumLineRef[] {
  const lines: U3129HonorariumLineRef[] = [];
  for (const sub of group.subprojectGroups) {
    for (const line of sub.lines) {
      if (u3129HonorariumLineHasEditableHourCells(line.row)) {
        lines.push(line);
      }
    }
  }
  return lines;
}

function findProjectGroupIndex(groups: readonly U3129HonorariumProjectGroup[], projectKey: string): number {
  return groups.findIndex((g) => g.projectKey === projectKey);
}

function findPreviousProjectIndexWithEditableLines(
  groups: readonly U3129HonorariumProjectGroup[],
  fromExclusive: number,
): number | null {
  for (let i = fromExclusive - 1; i >= 0; i -= 1) {
    if (flattenEditableLines(groups[i]).length > 0) {
      return i;
    }
  }
  return null;
}

function findNextProjectIndexWithEditableLines(
  groups: readonly U3129HonorariumProjectGroup[],
  fromExclusive: number,
): number | null {
  for (let i = fromExclusive + 1; i < groups.length; i += 1) {
    if (flattenEditableLines(groups[i]).length > 0) {
      return i;
    }
  }
  return null;
}

function resolveHorizontal(
  key: 'ArrowLeft' | 'ArrowRight',
  current: U3129HonorariumHourCellCoord,
  visibleDays: readonly U2000TimesheetWeekDay[],
): U3129HonorariumHourCellCoord | null {
  const dayIdx = visibleDays.indexOf(current.day);
  if (dayIdx < 0) {
    return null;
  }
  const delta = key === 'ArrowLeft' ? -1 : 1;
  const nextIdx = dayIdx + delta;
  if (nextIdx < 0 || nextIdx >= visibleDays.length) {
    return null;
  }
  return { projectKey: current.projectKey, index: current.index, day: visibleDays[nextIdx] };
}

function resolveVertical(
  key: 'ArrowUp' | 'ArrowDown',
  current: U3129HonorariumHourCellCoord,
  groups: readonly U3129HonorariumProjectGroup[],
): U3129HonorariumHourCellCoord | null {
  const groupIdx = findProjectGroupIndex(groups, current.projectKey);
  if (groupIdx < 0) {
    return null;
  }
  const group = groups[groupIdx];
  const lines = flattenEditableLines(group);
  const lineIdx = lines.findIndex((l) => l.index === current.index);
  if (lineIdx < 0) {
    return null;
  }

  if (key === 'ArrowUp') {
    if (lineIdx > 0) {
      return { projectKey: current.projectKey, index: lines[lineIdx - 1].index, day: current.day };
    }
    const prevProjectIdx = findPreviousProjectIndexWithEditableLines(groups, groupIdx);
    if (prevProjectIdx == null) {
      return null;
    }
    const prevLines = flattenEditableLines(groups[prevProjectIdx]);
    const last = prevLines[prevLines.length - 1];
    return { projectKey: groups[prevProjectIdx].projectKey, index: last.index, day: current.day };
  }

  if (lineIdx < lines.length - 1) {
    return { projectKey: current.projectKey, index: lines[lineIdx + 1].index, day: current.day };
  }
  const nextProjectIdx = findNextProjectIndexWithEditableLines(groups, groupIdx);
  if (nextProjectIdx == null) {
    return null;
  }
  const nextLines = flattenEditableLines(groups[nextProjectIdx]);
  return { projectKey: groups[nextProjectIdx].projectKey, index: nextLines[0].index, day: current.day };
}

/**
 * Prochaine cellule d’heures après une flèche, ou `null` pour rester sur place.
 * `projectGroups` = ordre d’affichage (tri projet / tâches déjà appliés).
 */
export function u3129ResolveHonorariumHourCellAfterArrow(
  key: string,
  projectGroups: readonly U3129HonorariumProjectGroup[],
  current: U3129HonorariumHourCellCoord,
  visibleDays: readonly U2000TimesheetWeekDay[],
): U3129HonorariumHourCellCoord | null {
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    return resolveHorizontal(key, current, visibleDays);
  }
  if (key === 'ArrowUp' || key === 'ArrowDown') {
    return resolveVertical(key, current, projectGroups);
  }
  return null;
}
